import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import Popper from "popper.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";

const ELLIPSIS_DROPDOWN_Z_INDEX = 1060;

const EllipsisDropdownSection = ({ children }) => children;

const EllipsisDropdownItem = React.forwardRef(
  ({ children, className = "", onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={`dropdown-item ${className}`.trim()}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  )
);

EllipsisDropdownSection.displayName = "EllipsisDropdownSection";
EllipsisDropdownItem.displayName = "EllipsisDropdownItem";

const isSectionElement = (child) =>
  React.isValidElement(child) && child.type === EllipsisDropdownSection;
const isItemElement = (child) =>
  React.isValidElement(child) && child.type === EllipsisDropdownItem;

const EllipsisDropdownMenu = ({
  className,
  onClose,
  placement,
  show,
  targetRef,
  children,
}) => {
  const menuRef = useRef(null);
  const popperRef = useRef(null);

  useEffect(() => {
    if (show && targetRef.current && menuRef.current) {
      popperRef.current = new Popper(targetRef.current, menuRef.current, {
        placement,
        modifiers: {
          preventOverflow: { boundariesElement: "viewport" },
          flip: { enabled: true },
        },
      });
    }

    return () => {
      if (popperRef.current) {
        popperRef.current.destroy();
        popperRef.current = null;
      }
    };
  }, [placement, show, targetRef]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        targetRef.current?.contains(event.target)
      ) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose({ returnFocus: true });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, show, targetRef]);

  if (!show) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={`dropdown-menu show ui-ellipsis-dropdown__menu ${className}`.trim()}
      style={{ position: "absolute", zIndex: ELLIPSIS_DROPDOWN_Z_INDEX }}
    >
      {children}
    </div>,
    document.body
  );
};

const EllipsisDropdown = ({
  ariaLabel = "Aktionen",
  children,
  className = "",
  menuClassName = "",
  placement = "bottom-end",
}) => {
  const [show, setShow] = useState(false);
  const toggleRef = useRef(null);

  const handleClose = useCallback(({ returnFocus = false } = {}) => {
    setShow(false);

    if (returnFocus) {
      toggleRef.current?.focus();
    }
  }, []);

  const handleToggle = useCallback(() => {
    setShow((prevShow) => !prevShow);
  }, []);

  const handleItemClick = useCallback(
    (originalOnClick) => (event) => {
      originalOnClick?.(event);
      handleClose();
    },
    [handleClose]
  );

  const sections = useMemo(
    () =>
      React.Children.toArray(children)
        .filter(isSectionElement)
        .map((section) =>
          React.Children.toArray(section.props.children).filter(isItemElement)
        )
        .filter((sectionItems) => sectionItems.length > 0),
    [children]
  );

  const menuItems = sections.flatMap((sectionItems, sectionIndex) => {
    const wrappedSectionItems = sectionItems.map((item, itemIndex) =>
      React.cloneElement(item, {
        key: `section-${sectionIndex}-${item.key ?? `item-${itemIndex}`}`,
        onClick: handleItemClick(item.props.onClick),
      })
    );

    if (sectionIndex === sections.length - 1) {
      return wrappedSectionItems;
    }

    return [
      ...wrappedSectionItems,
      <div
        key={`section-${sectionIndex}-divider`}
        className="dropdown-divider"
      />,
    ];
  });

  return (
    <div className={`dropdown ui-ellipsis-dropdown ${className}`.trim()}>
      <button
        ref={toggleRef}
        type="button"
        className="ui-ellipsis-dropdown__toggle"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={show}
        onClick={handleToggle}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>

      <EllipsisDropdownMenu
        className={menuClassName}
        onClose={handleClose}
        placement={placement}
        show={show}
        targetRef={toggleRef}
      >
        {menuItems}
      </EllipsisDropdownMenu>
    </div>
  );
};

export { EllipsisDropdownSection, EllipsisDropdownItem };
export default EllipsisDropdown;
